export type UrlKind = 'image' | 'driveFolder' | 'driveFile' | 'page';

export type MediaColumnKind = 'image' | 'drive';

export type MediaColumn = {
  index: number;
  header: string;
  kind: MediaColumnKind;
};

export type MediaColumnsDetection = {
  imageColumns: MediaColumn[];
  driveColumns: MediaColumn[];
  driveFolderUrl: string | null;
  driveSkippedReason: 'multipleDriveFolders' | null;
};

const URL_PATTERN = /https?:\/\/[^\s;|"'<>]+/gi;

const GLUED_URL_SEPARATOR = /,(?=https?:\/\/)/i;

const TRAILING_SEPARATORS = /[,;]+$/;

const IMAGE_EXTENSION = /\.(?:jpe?g|png|webp|gif|avif|heic|tiff?|svg)$/i;

const ANY_EXTENSION = /\.[a-z0-9]{2,5}$/i;

const IMAGE_FORMAT_VALUE = /^(?:jpe?g|png|webp|gif|avif|heic|tiff?|svg)$/i;

const IMAGE_FORMAT_PARAMS = ['format', 'fm', 'ext'];

const VIDEO_OR_PREVIEW_HOSTS = [
  'youtu.be',
  'youtube.com',
  'vimeo.com',
  'shopifypreview.com',
];

const PAGE_PATH = /\/(?:products|collections|p)\//i;

const MEDIA_TOKENS = new Set([
  'cdn',
  'img',
  'image',
  'images',
  'media',
  'static',
  'assets',
  'files',
  'uploads',
  'photos',
]);

const DRIVE_HOST = 'drive.google.com';

const DRIVE_FOLDER_PATH = /^\/drive\/(?:u\/\d+\/)?folders\/([\w-]+)/;

const DRIVE_OPEN_PATH = '/open';

const IMAGE_HEADER = /image|photo|visuel|picture/i;

const IMAGE_COLUMN_RATIO = 0.8;

const IMAGE_COLUMN_RATIO_WITH_HEADER = 0.5;

export function extractUrls(cell: string): string[] {
  return (cell.match(URL_PATTERN) ?? [])
    .flatMap((match) => match.split(GLUED_URL_SEPARATOR))
    .map((url) => url.replace(TRAILING_SEPARATORS, ''))
    .filter((url) => url !== '');
}

function parseUrl(url: string): URL | null {
  try {
    return new URL(url);
  } catch {
    return null;
  }
}

function hostMatches(host: string, candidate: string): boolean {
  return host === candidate || host.endsWith(`.${candidate}`);
}

export function toDriveFolderUrl(url: string): string | null {
  const parsed = parseUrl(url);
  if (!parsed || parsed.hostname.toLowerCase() !== DRIVE_HOST) return null;

  const folderId =
    DRIVE_FOLDER_PATH.exec(parsed.pathname)?.[1] ??
    (parsed.pathname === DRIVE_OPEN_PATH
      ? parsed.searchParams.get('id')
      : null);
  if (!folderId) return null;

  return `https://${DRIVE_HOST}/drive/folders/${folderId}`;
}

function hasImageFormatParam(parsed: URL): boolean {
  return IMAGE_FORMAT_PARAMS.some((param) => {
    const value = parsed.searchParams.get(param);
    return value !== null && IMAGE_FORMAT_VALUE.test(value);
  });
}

function hasMediaToken(parsed: URL): boolean {
  const tokens = `${parsed.hostname}${parsed.pathname}`
    .toLowerCase()
    .split(/[./_-]+/);
  return tokens.some((token) => MEDIA_TOKENS.has(token));
}

export function classifyUrl(url: string): UrlKind {
  const parsed = parseUrl(url);
  if (!parsed) return 'page';

  const host = parsed.hostname.toLowerCase();
  const path = parsed.pathname;

  if (host === DRIVE_HOST)
    return toDriveFolderUrl(url) ? 'driveFolder' : 'driveFile';
  if (VIDEO_OR_PREVIEW_HOSTS.some((candidate) => hostMatches(host, candidate)))
    return 'page';
  if (IMAGE_EXTENSION.test(path)) return 'image';
  if (hasImageFormatParam(parsed) || path.includes('/image/upload/'))
    return 'image';
  if (ANY_EXTENSION.test(path) || PAGE_PATH.test(path)) return 'page';
  if (hasMediaToken(parsed)) return 'image';

  return 'page';
}

function classifyColumn(
  header: string,
  urls: string[]
): MediaColumnKind | null {
  if (urls.length === 0) return null;

  const kinds = urls.map(classifyUrl);
  const count = (kind: UrlKind) =>
    kinds.filter((candidate) => candidate === kind).length;

  if (count('driveFolder') > urls.length / 2) return 'drive';

  const imageRatio = count('image') / urls.length;
  if (imageRatio >= IMAGE_COLUMN_RATIO) return 'image';
  if (imageRatio >= IMAGE_COLUMN_RATIO_WITH_HEADER && IMAGE_HEADER.test(header))
    return 'image';

  return null;
}

function distinctDriveFolderUrls(
  rows: string[][],
  driveColumns: MediaColumn[]
): string[] {
  const urls = rows.flatMap((row) =>
    driveColumns.flatMap((column) =>
      extractUrls(row[column.index] ?? '').flatMap((url) => {
        const folderUrl = toDriveFolderUrl(url);
        return folderUrl ? [folderUrl] : [];
      })
    )
  );
  return [...new Set(urls)];
}

export function detectMediaColumns(input: {
  headers: string[];
  rows: string[][];
  excludedIndexes?: ReadonlySet<number>;
}): MediaColumnsDetection {
  const { headers, rows, excludedIndexes } = input;

  const columns = headers.flatMap((header, index): MediaColumn[] => {
    if (excludedIndexes?.has(index)) return [];

    const urls = rows.flatMap((row) => extractUrls(row[index] ?? ''));
    const kind = classifyColumn(header, urls);

    return kind ? [{ index, header, kind }] : [];
  });
  const imageColumns = columns.filter((column) => column.kind === 'image');
  const driveColumns = columns.filter((column) => column.kind === 'drive');
  const folderUrls = distinctDriveFolderUrls(rows, driveColumns);

  return {
    imageColumns,
    driveColumns,
    driveFolderUrl: folderUrls.length === 1 ? (folderUrls[0] ?? null) : null,
    driveSkippedReason: folderUrls.length > 1 ? 'multipleDriveFolders' : null,
  };
}

export function mediaColumnsTakenOver(
  detection: MediaColumnsDetection
): MediaColumn[] {
  return [
    ...detection.imageColumns,
    ...(detection.driveFolderUrl ? detection.driveColumns : []),
  ];
}
